import Button, { buttonClassNames } from '@components/primitives/Button'
import { Text } from '@components/primitives/Text'
import { classMerge } from '@components/utilities/classMerge'
import DynamicRoutes from '@lib/routes/DynamicRoutes'
import PatientRoutes from '@lib/routes/PatientRoutes'
import absoluteUrl from '@lib/routes/helpers/absoluteUrl'
import urlWithQueryParams from '@lib/routes/helpers/urlWithQueryParams'
import type { TrackingSources } from '@lib/tracking/TrackingSources'
import type { Geolocation } from '@lib/types/Geolocation'
import getTreatmentTypeFromTrialInterventions from '@modules/interventions/helpers/getTreatmentTypeFromTrialInterventions'
import { getPreferredTrialTitleForAlgoliaTrial } from '@modules/trials/helpers/getPreferredTrialTitle'
import type { TrialForSummaryCard } from '@modules/trials/helpers/transformTrialForTrialSummaryCard'
import TrialIconAttributeSection from './TrialIconAttributeSection'
import TrialLinkWrapper from './TrialLinkWrapper'
import TrialSiteLocationImage from './TrialSiteLocationImage'
import TrialSummarySection from './TrialSummarySection'

export default function TrialCard({
  directToApply = false,
  hideButtomButton = false,
  nearestPoint,
  showLocation = true,
  trackingSource,
  trial,
  utmTrackingParamOverrides,
}: {
  directToApply?: boolean
  hideButtomButton?: boolean
  nearestPoint?: Geolocation
  showLocation?: boolean
  trackingSource?: TrackingSources
  trial: TrialForSummaryCard
  utmTrackingParamOverrides?: { [key: string]: string }
}) {
  const primaryTreatmentType =
    getTreatmentTypeFromTrialInterventions(trial) ?? ''
  const trialTitle = getPreferredTrialTitleForAlgoliaTrial(
    trial,
    trial.primaryCondition,
  )

  const trialCtaUrl = directToApply
    ? absoluteUrl(
        urlWithQueryParams(
          PatientRoutes.trialApplyStart(trial.slug?.toString()!),
          utmTrackingParamOverrides,
        ),
      )
    : absoluteUrl(DynamicRoutes.trial(trial.slug))

  return (
    <div className='flex w-full cursor-pointer flex-col items-start rounded-xl bg-white p-0 drop-shadow-lg hover:drop-shadow-xl'>
      <TrialSiteLocationImage trial={trial} />
      <div className='z-10 -mt-24 flex flex-col gap-y-3 p-4'>
        <div className='flex h-24 flex-col items-start justify-end text-left'>
          <Text
            className='line-clamp-1 text-neutral600'
            element='h4'
            styleName='p-small-strong'
            title={primaryTreatmentType}
            value={primaryTreatmentType}
          />
          {/* text component has some wacky behaviours with line-clamp. I stepped down to the metal here */}
          <h3 className='line-clamp-2 text-2xl font-bold text-black'>
            {trialTitle}
          </h3>
        </div>
        <TrialIconAttributeSection
          nearestPoint={nearestPoint}
          showLocation={showLocation}
          trial={trial}
        />
        <hr className='w-full border-neutral200' />
        <TrialSummarySection trial={trial} />
        {!hideButtomButton && (
          <div className='flex w-full flex-col items-center justify-center'>
            <div className='w-full'>
              <TrialLinkWrapper
                href={trialCtaUrl}
                newTab={true}
                trackingSource={trackingSource ? trackingSource : undefined}
              >
                <Button
                  className={classMerge(
                    directToApply
                      ? buttonClassNames.legacy.fill.primary
                      : buttonClassNames.legacy.fill.neutral,
                    'w-full rounded-full',
                  )}
                  value={directToApply ? 'Apply now' : 'View details'}
                />
              </TrialLinkWrapper>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
